import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import {
  ChartPie, Check, ChevronDown, ChevronLeft, ChevronRight, Plus,
  CircleAlert, Clock3, FolderOpen, Layers3,
  Loader2, Settings2, Smartphone, TestTube2, UserRound, Workflow, X,
} from 'lucide-react';
import {
  Cell, Pie, PieChart, ResponsiveContainer,
} from 'recharts';
import { useSearchParams } from 'react-router-dom';

import {
  createPatient,
  createSubproject,
  loadOrganizations,
  loadParentProjects,
  loadProjectPatients,
  loadSubprojectPatients,
  searchProjectPatients,
  loadSubprojects,
  searchPatients,
} from './api';
import { useXomicsAuth } from './AuthContext';
import { useLiveResource } from './useLive';
import {
  Avatar, Empty, Failed, Head, Loading, SearchField, ViewSwitch, WorkspaceTabs, XomicsSelect,
} from './parts';
import { errorMessage } from './ui';
import { tx } from './i18n';
import { codeLabel } from './labels';
import { PatientDetailModal } from './PatientDetailModal';
import { NewProjectDialog, ProjectSettingsDialog } from './ProjectSettingsDialog';

const ALL_SUBPROJECTS = 'all';
const PAGE = 50;
const SEARCH_DELAY_MS = 250;
const SEARCH_MIN_LENGTH = 3;
const PROJECT_PREVIEW_COUNT = 3;
const COUNT_PATIENTS = 'patients';
const COUNT_VISITS = 'visits';
const SAMPLE_CHART_COLORS = Object.freeze([
  '#203a85', '#5069a3', '#687fb3', '#8095c3', '#98aad2',
  '#afbee0', '#c2cfea', '#d2dcf1', '#e0e7f6', '#ecf1fa',
]);

const MATCH_LABEL_KEY = Object.freeze({
  name: 'ui.projects.match_name',
  email: 'ui.projects.match_email',
  phone: 'ui.projects.match_phone',
  dateOfBirth: 'ui.projects.match_date_of_birth',
  personalId: 'ui.projects.match_personal_id',
});

function date(value) {
  const timestamp = Number(value);
  if (!Number.isFinite(timestamp) || timestamp <= 0) return '—';
  return new Date(timestamp).toLocaleDateString(undefined, {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

function projectStats(project) {
  const samples = Array.isArray(project.samples) ? project.samples : [];
  const runs = Array.isArray(project.runs) ? project.runs : [];
  const pipelines = new Set();
  let uploads = 0;
  let runCount = 0;
  let complete = 0;
  let active = 0;

  samples.forEach((sample) => { uploads += Number(sample.uploads) || 0; });
  runs.forEach((run) => {
    const totals = analysisTotals(run);
    if (totals.total < 1) return;
    pipelines.add(run.pipeline);
    runCount += totals.total;
    complete += totals.complete;
    active += totals.active;
  });
  return {
    sampleTypes: samples.length,
    uploads,
    pipelineCount: pipelines.size,
    pipelineNames: Array.from(pipelines),
    runs: runCount,
    complete,
    active,
  };
}

function analysisTotals(run) {
  const states = run?.states || {};
  const complete = Number(states.complete) || 0;
  const failed = (Number(states.failed) || 0) + (Number(states.cancelled) || 0);
  const declaredTotal = Number(run?.runs) || 0;
  const countedTotal = Object.values(states).reduce(
    (sum, value) => sum + (Number(value) || 0), 0,
  );
  const total = Math.max(0, Math.max(declaredTotal, countedTotal, complete + failed) - failed);
  return {
    total,
    complete,
    active: Math.max(0, total - complete),
  };
}

function coveragePercent(count, total) {
  if (total < 1) return 0;
  return Math.min(100, Math.max(0, (count / total) * 100));
}

function coveragePercentLabel(count, total) {
  const percent = coveragePercent(count, total);
  if (count > 0 && percent < 1) return '<1%';
  return `${Math.round(percent).toLocaleString()}%`;
}

function coverageCount(value, countMode) {
  return Number(countMode === COUNT_VISITS ? value?.visitCount : value?.patientCount) || 0;
}

function sampleCoverage(project, countMode) {
  return (Array.isArray(project.samples) ? project.samples : [])
    .map((sample) => ({
      code: sample.code,
      name: sample.name || sample.code,
      count: coverageCount(sample, countMode),
    }))
    .filter((sample) => sample.count > 0)
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
}

function analysisCoverage(project, countMode) {
  const sampleNames = new Map(
    (Array.isArray(project.samples) ? project.samples : [])
      .map((sample) => [sample.code, sample.name || sample.code]),
  );
  const byPipeline = new Map();
  (Array.isArray(project.analyses) ? project.analyses : []).forEach((analysis) => {
    const count = coverageCount(analysis, countMode);
    if (count < 1) return;
    const sampleCode = String(analysis.sampleType || '').trim();
    const pipelineKey = String(analysis.pipelineId || analysis.pipeline);
    if (!byPipeline.has(pipelineKey)) {
      byPipeline.set(pipelineKey, {
        pipelineId: analysis.pipelineId,
        pipeline: analysis.pipeline,
        count: 0,
        samples: new Map(),
      });
    }
    const pipeline = byPipeline.get(pipelineKey);
    pipeline.count += count;
    const { samples } = pipeline;
    const sample = samples.get(sampleCode) || {
      code: sampleCode,
      name: sampleNames.get(sampleCode) || sampleCode,
      count: 0,
    };
    sample.count += count;
    samples.set(sampleCode, sample);
  });
  return [...byPipeline.values()]
    .map((pipeline) => ({
      ...pipeline,
      samples: [...pipeline.samples.values()].sort(
        (a, b) => b.count - a.count || a.name.localeCompare(b.name),
      ),
    }))
    .sort((a, b) => a.pipeline.localeCompare(b.pipeline));
}

// Ascending: the oldest project, which is the lowest id, reads first.
function compareProjectIds(left, right) {
  const difference = Number(left.id) - Number(right.id);
  if (Number.isFinite(difference) && difference !== 0) return difference;
  return String(left.id).localeCompare(String(right.id), undefined, { numeric: true });
}

function aggregateProjects(projects, countMode) {
  const samples = new Map();
  const analyses = new Map();
  const totals = {
    projects: projects.length,
    subprojects: 0,
    patients: 0,
    samples: 0,
    runs: 0,
    analyses: 0,
  };

  projects.forEach((project) => {
    const stats = projectStats(project);
    totals.subprojects += Number(project.subProjectCount) || 0;
    totals.patients += Number(project.patientCount) || 0;
    totals.runs += stats.runs;

    (Array.isArray(project.samples) ? project.samples : []).forEach((sample) => {
      const code = String(sample.code || '').trim();
      if (code === '') return;
      const current = samples.get(code) || {
        code,
        name: sample.name || code,
        count: 0,
        projects: [],
      };
      const count = coverageCount(sample, countMode);
      current.count += count;
      if (count > 0) {
        current.projects.push({
          id: project.id,
          name: project.name,
          count,
        });
      }
      samples.set(code, current);
    });

    (Array.isArray(project.analyses) ? project.analyses : []).forEach((analysis) => {
      const count = coverageCount(analysis, countMode);
      if (count < 1) return;
      const sampleCode = String(analysis.sampleType || '').trim();
      const pipeline = String(analysis.pipeline || analysis.pipelineId || '').trim();
      const key = `${sampleCode}:${analysis.pipelineId || pipeline}`;
      const current = analyses.get(key) || {
        sampleCode,
        pipeline,
        pipelineId: analysis.pipelineId,
        count: 0,
      };
      current.count += count;
      analyses.set(key, current);
    });
  });

  const sampleItems = [...samples.values()]
    .filter((sample) => sample.count > 0)
    .map((sample) => ({
      ...sample,
      projects: sample.projects.sort(
        (a, b) => b.count - a.count || compareProjectIds(a, b),
      ),
    }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  totals.samples = sampleItems.reduce((sum, sample) => sum + sample.count, 0);
  const sampleNames = new Map(sampleItems.map((sample) => [sample.code, sample.name]));
  const analysisGroups = [...analyses.values()].reduce((groups, analysis) => {
    const key = String(analysis.pipelineId || analysis.pipeline);
    if (!groups.has(key)) {
      groups.set(key, {
        pipelineId: analysis.pipelineId,
        pipeline: analysis.pipeline,
        count: 0,
        samples: [],
      });
    }
    const group = groups.get(key);
    group.count += analysis.count;
    group.samples.push({
      code: analysis.sampleCode,
      name: sampleNames.get(analysis.sampleCode) || analysis.sampleCode,
      count: analysis.count,
    });
    return groups;
  }, new Map());
  totals.analyses = [...analyses.values()].reduce(
    (sum, analysis) => sum + analysis.count, 0,
  );

  return {
    totals,
    samples: sampleItems,
    analyses: [...analysisGroups.values()]
      .map((pipeline) => ({
        ...pipeline,
        samples: pipeline.samples.sort(
          (a, b) => b.count - a.count || a.name.localeCompare(b.name),
        ),
      }))
      .sort((a, b) => b.count - a.count
        || a.pipeline.localeCompare(b.pipeline)),
  };
}

function CursorPager({
  pageIndex, count, total, hasMore, busy, onPrevious, onNext,
}) {
  const from = count === 0 ? 0 : (pageIndex * PAGE) + 1;
  const unboundedTo = (pageIndex * PAGE) + count;
  const to = total === null ? unboundedTo : Math.min(unboundedTo, total);
  return (
    <footer className="xo-project-pager">
      <p aria-live="polite">
        {tx('ui.projects.showing_163d8174')} {from}-{to}
        {total === null ? '' : tx('ui.projects.of_total_56cb6929', { total })}
      </p>
      <div>
        <button type="button" disabled={busy || pageIndex === 0} onClick={onPrevious}>
          <ChevronLeft aria-hidden="true" />{tx('ui.projects.previous_50f94286')}
        </button>
        <button type="button" disabled={busy || !hasMore} onClick={onNext}>
          {tx('ui.projects.next_bc981983')}<ChevronRight aria-hidden="true" />
        </button>
      </div>
    </footer>
  );
}

function CoverageRow({ name, code = '', count, total }) {
  const percent = coveragePercent(count, total);
  return (
    <div className="xo-project-coverage-row">
      <span className="xo-project-coverage-name">
        <strong>{name}</strong>
        {code && <small>{code}</small>}
      </span>
      <span className="xo-project-coverage-value">
        <strong>{count.toLocaleString()} / {total.toLocaleString()}</strong>
        <small>{coveragePercentLabel(count, total)}</small>
      </span>
      <span className="xo-project-coverage-track" role="progressbar"
        aria-label={name} aria-valuemin="0" aria-valuemax={total} aria-valuenow={count}>
        <i style={{ width: `${percent}%` }} />
      </span>
    </div>
  );
}

function ProjectSubprojectCarousel({
  project, subprojects, loading, error, onRetry, onSelect,
}) {
  const [edges, setEdges] = useState({ start: true, end: false });
  const rail = useRef(null);
  const status = loading ? 'loading' : error ? 'failed' : 'ready';

  const readEdges = useCallback(() => {
    const element = rail.current;
    if (element === null) return;
    const maximum = Math.max(0, element.scrollWidth - element.clientWidth);
    const position = Math.abs(element.scrollLeft);
    setEdges({ start: position <= 2, end: position >= maximum - 2 });
  }, []);

  const scroll = (direction) => {
    const element = rail.current;
    if (element === null) return;
    const rtl = document.documentElement.dir === 'rtl' ? -1 : 1;
    element.scrollBy({
      left: direction * rtl * Math.max(180, element.clientWidth * 0.72),
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    const frame = window.requestAnimationFrame(readEdges);
    window.addEventListener('resize', readEdges);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', readEdges);
    };
  }, [readEdges, subprojects.length]);

  if (Number(project.subProjectCount) < 1) return null;
  return (
    <div className={'xo-project-subproject-carousel is-' + status}
      role="group" aria-label={tx('ui.projects.subprojects_4884f24d')}>
      <button type="button" disabled={status !== 'ready' || edges.start}
        aria-label={tx('ui.projects.previous_50f94286')}
        onClick={() => scroll(-1)}>
        <ChevronLeft aria-hidden="true" />
      </button>
      <div className="xo-project-subproject-rail" ref={rail} onScroll={readEdges}>
        {status === 'loading' && (
          <span className="xo-project-subproject-loading">
            <Loader2 className="xo-spin" aria-hidden="true" />
            {tx('ui.projects.reading_subprojects')}
          </span>
        )}
        {status === 'failed' && (
          <button type="button" className="xo-project-subproject-retry"
            onClick={onRetry}>
            <CircleAlert aria-hidden="true" />{tx('ui.parts.try_again_042c862e')}
          </button>
        )}
        {subprojects.map((subproject) => (
          <button type="button" className="xo-project-subproject-slide" key={subproject.id}
            aria-label={tx('ui.projects.subproject_subprojectid_f60abd82', {
              subProjectId: subproject.projectId,
            })}
            onClick={() => onSelect(project, subproject.projectId)}>
            <strong>{subproject.projectId}</strong>
            <small>{subproject.patientCount.toLocaleString()} {tx('ui.projects.patients_bade30a4')}</small>
          </button>
        ))}
      </div>
      <button type="button" disabled={status !== 'ready' || edges.end}
        aria-label={tx('ui.projects.next_bc981983')}
        onClick={() => scroll(1)}>
        <ChevronRight aria-hidden="true" />
      </button>
    </div>
  );
}

function ProjectCard({
  project, subprojects, subprojectsLoading, subprojectsError, onRetrySubprojects, onSelect,
  countMode, onSettings,
}) {
  const [view, setView] = useState('samples');
  const samples = sampleCoverage(project, countMode);
  const analysisGroups = analysisCoverage(project, countMode);
  const analysisCount = analysisGroups.length;
  const sampleCount = (Array.isArray(project.samples) ? project.samples : [])
    .reduce((sum, sample) => sum + coverageCount(sample, countMode), 0);
  const totalPatients = Number(project.patientCount) || 0;
  const sampleDenominator = countMode === COUNT_VISITS ? sampleCount : totalPatients;
  return (
    <article className="xo-project-card">
      <header className="xo-project-card-head">
        <button type="button" className="xo-project-board-open"
          aria-label={tx('ui.projects.open_project_named', { name: project.name })}
          onClick={() => onSelect(project)}>
          <span className="xo-project-board-icon" aria-hidden="true"><FolderOpen /></span>
          <span>
            <strong>{project.name}</strong>
            <small>#{project.id} · {tx('ui.projects.project_created_on', { date: date(project.createdAt) })}</small>
          </span>
          <ChevronRight aria-hidden="true" />
        </button>
        <div className="xo-project-card-toolbar">
          {onSettings && <button type="button" className="xo-project-config-button" onClick={() => onSettings(project)}
            aria-label={tx('ui.projects.settings.for_project', { name: project.name })} title={tx('ui.projects.settings.title')}>
            <Settings2 aria-hidden="true" />
          </button>}
          <dl className="xo-project-card-scale">
            <div><dt>{tx('ui.projects.patients_bade30a4')}</dt>
              <dd>{totalPatients.toLocaleString()}</dd></div>
            <div><dt>{tx('ui.projects.subprojects_4884f24d')}</dt>
              <dd>{Number(project.subProjectCount || 0).toLocaleString()}</dd></div>
          </dl>
          <div className="xo-project-coverage-tabs" role="tablist">
            <button type="button" role="tab" aria-selected={view === 'samples'}
              className={view === 'samples' ? 'is-active' : ''} onClick={() => setView('samples')}>
              <TestTube2 aria-hidden="true" />
              <span>{tx('ui.projects.samples_0fb59422')}</span><b>{sampleCount.toLocaleString()}</b>
            </button>
            <button type="button" role="tab" aria-selected={view === 'analyses'}
              className={view === 'analyses' ? 'is-active' : ''} onClick={() => setView('analyses')}>
              <Workflow aria-hidden="true" />
              <span>{tx('ui.nav.pipelines_c5df1e14')}</span><b>{analysisCount}</b>
            </button>
          </div>
        </div>
        <ProjectSubprojectCarousel project={project} subprojects={subprojects}
          loading={subprojectsLoading} error={subprojectsError}
          onRetry={onRetrySubprojects} onSelect={onSelect} />
      </header>
      <div className="xo-project-card-body">
        <div className={'xo-project-coverage-content is-' + view} role="tabpanel">
          {view === 'samples' && samples.length === 0 && (
            <div className="xo-project-coverage-empty">
              <TestTube2 aria-hidden="true" />
              <span>{tx('ui.projects.nothing_uploaded_c05ea45e')}</span>
            </div>
          )}
          {view === 'samples' && samples.map((sample) => (
            <CoverageRow key={sample.code} name={sample.name} code={sample.code}
              count={sample.count} total={sampleDenominator} />
          ))}
          {view === 'analyses' && analysisGroups.length === 0 && (
            <div className="xo-project-coverage-empty">
              <Workflow aria-hidden="true" />
              <span>{tx('ui.projects.nothing_run_4a071f44')}</span>
            </div>
          )}
          {view === 'analyses' && analysisGroups.map((pipeline) => (
            <section className="xo-project-coverage-group"
              key={pipeline.pipelineId || pipeline.pipeline}>
              <header><Workflow aria-hidden="true" />
                <strong>{pipeline.pipeline}</strong>
                <small>{pipeline.count.toLocaleString()} {tx('ui.values.completed').toLowerCase()}</small>
              </header>
              {pipeline.samples.map((sample) => (
                <CoverageRow key={`${pipeline.pipelineId || pipeline.pipeline}:${sample.code}`}
                  name={sample.name} code={sample.code}
                  count={sample.count} total={pipeline.count} />
              ))}
            </section>
          ))}
        </div>
      </div>
    </article>
  );
}

function ProjectList({
  projects, subprojects, subprojectsLoading, subprojectsError, onRetrySubprojects, onSelect,
  countMode, onSettings,
}) {
  return (
    <div className="xo-project-list">
      {projects.map((project) => (
        <ProjectCard key={project.id} project={project}
          subprojects={subprojects.filter((item) => item.parentProjectId === project.id)}
          subprojectsLoading={subprojectsLoading} subprojectsError={subprojectsError}
          onRetrySubprojects={onRetrySubprojects} onSelect={onSelect}
          countMode={countMode} onSettings={onSettings} />
      ))}
    </div>
  );
}

function SubprojectCard({ subproject, onSelect, countMode }) {
  const [view, setView] = useState('samples');
  const samples = sampleCoverage(subproject, countMode);
  const analysisGroups = analysisCoverage(subproject, countMode);
  const sampleCount = (Array.isArray(subproject.samples) ? subproject.samples : [])
    .reduce((sum, sample) => sum + coverageCount(sample, countMode), 0);
  const totalPatients = Number(subproject.patientCount) || 0;
  const sampleDenominator = countMode === COUNT_VISITS ? sampleCount : totalPatients;
  return (
    <article className="xo-project-card is-subproject">
      <header className="xo-project-card-head">
        <button type="button" className="xo-project-board-open"
          aria-label={tx('ui.projects.subproject_subprojectid_f60abd82', {
            subProjectId: subproject.projectId,
          })}
          onClick={() => onSelect(
            subproject.parentProject, subproject.projectId,
          )}>
          <span className="xo-project-board-icon" aria-hidden="true"><Layers3 /></span>
          <span>
            <strong>{subproject.projectId}</strong>
            <small>{subproject.parentProject.name} · #{subproject.parentProject.id}
              {' · '}{tx('ui.projects.project_created_on', {
                date: date(subproject.createdAt),
              })}{subproject.organization ? ` · ${subproject.organization.name}` : ''}</small>
            {subproject.description && (
              <span className="xo-project-board-description">{subproject.description}</span>
            )}
          </span>
          <ChevronRight aria-hidden="true" />
        </button>
        <div className="xo-project-card-toolbar">
          <dl className="xo-project-card-scale">
            <div><dt>{tx('ui.projects.patients_bade30a4')}</dt>
              <dd>{totalPatients.toLocaleString()}</dd></div>
            <div><dt>{tx('ui.projects.projects_53e890d5')}</dt>
              <dd>#{subproject.parentProject.id}</dd></div>
          </dl>
          <div className="xo-project-coverage-tabs" role="tablist">
            <button type="button" role="tab" aria-selected={view === 'samples'}
              className={view === 'samples' ? 'is-active' : ''}
              onClick={() => setView('samples')}>
              <TestTube2 aria-hidden="true" />
              <span>{tx('ui.projects.samples_0fb59422')}</span>
              <b>{sampleCount.toLocaleString()}</b>
            </button>
            <button type="button" role="tab" aria-selected={view === 'analyses'}
              className={view === 'analyses' ? 'is-active' : ''}
              onClick={() => setView('analyses')}>
              <Workflow aria-hidden="true" />
              <span>{tx('ui.nav.pipelines_c5df1e14')}</span>
              <b>{analysisGroups.length.toLocaleString()}</b>
            </button>
          </div>
        </div>
      </header>
      <div className="xo-project-card-body">
        <div className={'xo-project-coverage-content is-' + view} role="tabpanel">
          {view === 'samples' && samples.length === 0 && (
            <div className="xo-project-coverage-empty">
              <TestTube2 aria-hidden="true" />
              <span>{tx('ui.projects.nothing_uploaded_c05ea45e')}</span>
            </div>
          )}
          {view === 'samples' && samples.map((sample) => (
            <CoverageRow key={sample.code} name={sample.name} code={sample.code}
              count={sample.count} total={sampleDenominator} />
          ))}
          {view === 'analyses' && analysisGroups.length === 0 && (
            <div className="xo-project-coverage-empty">
              <Workflow aria-hidden="true" />
              <span>{tx('ui.projects.nothing_run_4a071f44')}</span>
            </div>
          )}
          {view === 'analyses' && analysisGroups.map((pipeline) => (
            <section className="xo-project-coverage-group"
              key={pipeline.pipelineId || pipeline.pipeline}>
              <header><Workflow aria-hidden="true" />
                <strong>{pipeline.pipeline}</strong>
                <small>{pipeline.count.toLocaleString()}
                  {' '}{tx('ui.values.completed').toLowerCase()}</small>
              </header>
              {pipeline.samples.map((sample) => (
                <CoverageRow key={`${pipeline.pipelineId || pipeline.pipeline}:${sample.code}`}
                  name={sample.name} code={sample.code}
                  count={sample.count} total={pipeline.count} />
              ))}
            </section>
          ))}
        </div>
      </div>
    </article>
  );
}

function ProjectCountMode({ value, onChange }) {
  const options = [
    {
      id: COUNT_PATIENTS,
      label: tx('ui.projects.patients_bade30a4'),
      Icon: UserRound,
    },
    {
      id: COUNT_VISITS,
      label: tx('ui.nav.visits_ec7b4ab4'),
      Icon: Clock3,
    },
  ];
  return (
    <div className="xo-project-count-mode" role="group"
      aria-label={tx("ui.projects.label_label2_ae62eb50", { label: options[0].label, label2: options[1].label })}>
      {options.map(({ id, label, Icon }) => (
        <button type="button" key={id} aria-pressed={value === id}
          className={value === id ? 'is-active' : ''} onClick={() => onChange(id)}>
          <Icon aria-hidden="true" /><span>{label}</span>
        </button>
      ))}
    </div>
  );
}

function ProjectViewTabs({ value, projectCount, subprojectCount, onChange }) {
  const views = [
    { value: 'overview', label: tx('ui.uploads.overview'), Icon: Workflow },
    {
      value: 'projects', label: tx('ui.projects.projects_53e890d5'),
      count: projectCount, Icon: FolderOpen,
    },
    {
      value: 'subprojects', label: tx('ui.projects.subprojects_4884f24d'),
      count: subprojectCount, Icon: Layers3,
    },
  ];
  return (
    <WorkspaceTabs className="xo-project-view-tabs" value={value} tabs={views}
      onChange={onChange} label={tx('ui.projects.projects_53e890d5')}
      idPrefix="xo-project-view" panelId="xo-project-view-panel" />
  );
}

const CHART_RADIAN = Math.PI / 180;
const SAMPLE_CHART_PADDING_ANGLE = 2;
const SAMPLE_CHART_LABEL_LINE_HEIGHT = 17;
const SAMPLE_CHART_LABEL_META_HEIGHT = 16;
const SAMPLE_CHART_LABEL_GAP = 6;

function sampleChartPoint(cx, cy, radius, midAngle) {
  return {
    x: cx + (radius * Math.cos(-midAngle * CHART_RADIAN)),
    y: cy + (radius * Math.sin(-midAngle * CHART_RADIAN)),
  };
}

function wrapChartLabel(value, maximum = 14) {
  const words = String(value || '').trim().split(/\s+/).filter(Boolean);
  const lines = [];
  words.forEach((word) => {
    const current = lines[lines.length - 1];
    if (current && `${current} ${word}`.length <= maximum) {
      lines[lines.length - 1] = `${current} ${word}`;
    } else {
      lines.push(word);
    }
  });
  return lines.length > 0 ? lines : ['—'];
}

function resolveSampleChartLabelSide(entries, minimumY, maximumY) {
  if (entries.length === 0) return [];
  const ordered = [...entries].sort((left, right) => left.naturalY - right.naturalY);
  const availableHeight = maximumY - minimumY;
  const labelHeight = ordered.reduce((sum, entry) => sum + entry.height, 0);
  const gap = ordered.length === 1 ? 0 : Math.max(0, Math.min(
    SAMPLE_CHART_LABEL_GAP,
    (availableHeight - labelHeight) / (ordered.length - 1),
  ));
  const offsets = [0];
  for (let index = 1; index < ordered.length; index += 1) {
    offsets[index] = offsets[index - 1]
      + ((ordered[index - 1].height + ordered[index].height) / 2)
      + gap;
  }

  // Pool-adjacent-violators gives the minimum total displacement that satisfies
  // every vertical gap. Labels outside a colliding cluster retain naturalY.
  const blocks = [];
  ordered.forEach((entry, index) => {
    blocks.push({ start: index, end: index, sum: entry.naturalY - offsets[index], count: 1 });
    while (blocks.length > 1) {
      const current = blocks[blocks.length - 1];
      const previous = blocks[blocks.length - 2];
      if ((previous.sum / previous.count) <= (current.sum / current.count)) break;
      blocks.splice(blocks.length - 2, 2, {
        start: previous.start,
        end: current.end,
        sum: previous.sum + current.sum,
        count: previous.count + current.count,
      });
    }
  });

  const lowerBound = minimumY + (ordered[0].height / 2);
  const finalIndex = ordered.length - 1;
  const upperBound = maximumY - (ordered[finalIndex].height / 2) - offsets[finalIndex];
  if (lowerBound > upperBound) return ordered.map((entry) => ({ ...entry, y: entry.naturalY }));

  const resolved = new Array(ordered.length);
  blocks.forEach((block) => {
    const origin = Math.max(lowerBound, Math.min(upperBound, block.sum / block.count));
    for (let index = block.start; index <= block.end; index += 1) {
      resolved[index] = { ...ordered[index], y: origin + offsets[index] };
    }
  });
  return resolved;
}

function layoutSampleChartLabels(samples, width, height) {
  if (samples.length === 0 || width < 1 || height < 1) return new Map();
  const total = samples.reduce((sum, sample) => sum + sample.count, 0);
  const padding = samples.length > 1 ? SAMPLE_CHART_PADDING_ANGLE : 0;
  const availableAngle = 360 - (padding * samples.length);
  const outerRadius = Math.min(Math.max(0, width - 184), Math.max(0, height - 60)) * 0.4;
  const centerY = height / 2;
  let cursor = 0;
  const entries = samples.map((sample) => {
    const angle = (sample.count / total) * availableAngle;
    const midAngle = cursor + (angle / 2);
    cursor += angle + padding;
    const lines = wrapChartLabel(sample.name);
    return {
      code: sample.code,
      naturalY: sampleChartPoint(0, centerY, outerRadius + 31, midAngle).y,
      side: Math.cos(-midAngle * CHART_RADIAN) >= 0 ? 'right' : 'left',
      height: (lines.length * SAMPLE_CHART_LABEL_LINE_HEIGHT) + SAMPLE_CHART_LABEL_META_HEIGHT,
    };
  });
  const positions = new Map();
  ['left', 'right'].forEach((side) => {
    resolveSampleChartLabelSide(
      entries.filter((entry) => entry.side === side),
      10,
      height - 10,
    ).forEach((entry) => positions.set(entry.code, { y: entry.y }));
  });
  return positions;
}

function SampleChartLabelLine({
  cx, cy, midAngle, outerRadius, position,
}) {
  const start = sampleChartPoint(cx, cy, outerRadius + 4, midAngle);
  const elbow = sampleChartPoint(cx, cy, outerRadius + 31, midAngle);
  const labelY = position?.y ?? elbow.y;
  const isRight = elbow.x >= cx;
  const direction = isRight ? 1 : -1;
  const bendX = elbow.x + (direction * 10);
  const endX = elbow.x + (direction * 22);
  return (
    <path className="xo-project-sample-chart-line"
      d={`M ${start.x} ${start.y} L ${elbow.x} ${elbow.y} L ${bendX} ${labelY} L ${endX} ${labelY}`}
      markerStart="url(#xo-project-sample-arrow)" />
  );
}

function SampleChartLabel({
  cx, cy, midAngle, outerRadius, payload, percent, position,
}) {
  if (!payload) return null;
  const elbow = sampleChartPoint(cx, cy, outerRadius + 31, midAngle);
  const labelY = position?.y ?? elbow.y;
  const isRight = elbow.x >= cx;
  const x = elbow.x + (isRight ? 30 : -30);
  const lines = wrapChartLabel(payload.name);
  const firstLineOffset = -0.2 - ((lines.length - 1) * 0.62);
  return (
    <text x={x} y={labelY} textAnchor={isRight ? 'start' : 'end'}
      className="xo-project-sample-chart-label">
      {lines.map((line, lineIndex) => (
        <tspan x={x} dy={`${lineIndex === 0 ? firstLineOffset : 1.2}em`}
          key={`${line}-${lineIndex}`}>
          {line}
        </tspan>
      ))}
      <tspan x={x} dy="1.35em" className="xo-project-sample-chart-label-meta">
        {payload.count.toLocaleString()} · {Math.round(percent * 100).toLocaleString()}%
      </tspan>
    </text>
  );
}

function SampleSplitChart({ samples }) {
  const chartSamples = samples
    .filter((sample) => sample.count > 0)
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
    .map((sample, index) => ({
      ...sample,
      fill: SAMPLE_CHART_COLORS[index % SAMPLE_CHART_COLORS.length],
    }));
  const total = chartSamples.reduce((sum, sample) => sum + sample.count, 0);
  const [chartSize, setChartSize] = useState({ width: 0, height: 0 });
  const labelPositions = layoutSampleChartLabels(
    chartSamples, chartSize.width, chartSize.height,
  );
  const measureChart = useCallback((width, height) => {
    setChartSize((current) => (
      current.width === width && current.height === height ? current : { width, height }
    ));
  }, []);

  if (total < 1) return null;
  return (
    <div className="xo-project-sample-split" role="img"
      aria-label={tx("ui.projects.value_value2_29c8b98a", { value: total.toLocaleString(), value2: tx('ui.projects.samples_0fb59422') })}>
      <ResponsiveContainer width="100%" height="100%" onResize={measureChart}>
        <PieChart margin={{ top: 30, right: 92, bottom: 30, left: 92 }}>
          <defs>
            <marker id="xo-project-sample-arrow" viewBox="0 0 8 8"
              refX="4" refY="4" markerWidth="5" markerHeight="5"
              orient="auto-start-reverse">
              <path className="xo-project-sample-chart-arrow" d="M 0 0 L 8 4 L 0 8 z" />
            </marker>
          </defs>
          <Pie data={chartSamples} dataKey="count" nameKey="name"
            cx="50%" cy="50%" innerRadius="54%" outerRadius="80%"
            startAngle={0} endAngle={360}
            paddingAngle={SAMPLE_CHART_PADDING_ANGLE} cornerRadius={5}
            labelLine={(props) => <SampleChartLabelLine {...props}
              position={labelPositions.get(props.payload?.code)} />}
            label={(props) => <SampleChartLabel {...props}
              position={labelPositions.get(props.payload?.code)} />}
            isAnimationActive={false}>
            {chartSamples.map((sample) => (
              <Cell key={sample.code} fill={sample.fill} stroke="#f4f6fb" strokeWidth={2} />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
      <span className="xo-project-sample-chart-center">
        <strong>{total.toLocaleString()}</strong><small>{tx('ui.projects.samples_0fb59422')}</small>
      </span>
    </div>
  );
}

function SampleProjectLeaders({ sample, onOpen, groupLabel }) {
  const leaders = sample.projects.slice(0, PROJECT_PREVIEW_COUNT);
  if (leaders.length === 0) return null;
  return (
    <div className="xo-project-sample-leaders">
      <span>{groupLabel}</span>
      <ol>
        {leaders.map((project) => (
          <li key={project.id}>
            <span><strong>{project.name}</strong><small>#{project.id}</small></span>
            <b>{project.count.toLocaleString()}</b>
          </li>
        ))}
      </ol>
      <button type="button" onClick={onOpen}>
        <span>{groupLabel}</span>
        <b>{sample.projects.length.toLocaleString()}</b>
        <ChevronRight aria-hidden="true" />
      </button>
    </div>
  );
}

function SampleProjectsModal({ sample, onClose, groupLabel }) {
  const titleId = `xo-project-sample-breakdown-${sample.code}`;
  useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [onClose]);

  return (
    <div className="xo-project-breakdown-backdrop" role="presentation"
      onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className="xo-project-breakdown-modal" role="dialog" aria-modal="true"
        aria-labelledby={titleId}>
        <header>
          <span aria-hidden="true"><TestTube2 /></span>
          <div>
            <small>{groupLabel}</small>
            <h2 id={titleId}>{sample.name}</h2>
            <p>{sample.count.toLocaleString()} {tx('ui.projects.samples_0fb59422')}
              {' · '}{sample.projects.length.toLocaleString()} {groupLabel}</p>
          </div>
          <button type="button" aria-label={tx('ui.products.close_bbfa773e')} onClick={onClose}>
            <X aria-hidden="true" />
          </button>
        </header>
        <div className="xo-project-breakdown-list">
          {sample.projects.map((project) => (
            <CoverageRow key={project.id} name={project.name} code={`#${project.id}`}
              count={project.count} total={sample.count} />
          ))}
        </div>
      </section>
    </div>
  );
}

// The project dashboard is the estate dashboard over one project's
// subprojects. It lives behind a button rather than above the patients: the
// detail screen is there to find a patient, and the charts would push the
// tiles off the first screen.
function ProjectInformationModal({
  project, subprojects, countMode, onCountMode, onClose,
}) {
  const titleId = `xo-project-information-${project.id}`;
  useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [onClose]);

  return (
    <div className="xo-project-breakdown-backdrop" role="presentation"
      onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className="xo-project-breakdown-modal is-project-information" role="dialog"
        aria-modal="true" aria-labelledby={titleId}>
        <header>
          <span aria-hidden="true"><ChartPie /></span>
          <div>
            <small>{tx('ui.projects.project_information')}</small>
            <h2 id={titleId}>{project.name}</h2>
            <p>{tx('ui.projects.project_id_every_patient_across_its_subprojects_10dbc604', {
              id: project.id,
            })}</p>
          </div>
          <button type="button" aria-label={tx('ui.products.close_bbfa773e')} onClick={onClose}>
            <X aria-hidden="true" />
          </button>
        </header>
        <div className="xo-project-information-body">
          <div className="xo-project-catalogue-controls">
            <ProjectCountMode value={countMode} onChange={onCountMode} />
          </div>
          <ProjectOverview projects={subprojects} countMode={countMode} scope="project" />
        </div>
      </section>
    </div>
  );
}

// The same dashboard serves the estate and one project. At project scope the
// rows handed in are that project's subprojects, so the group a sample is
// attributed to is a subproject and there is no outer project count to show.
function ProjectOverview({ projects, countMode, scope = 'estate' }) {
  const [sampleBreakdownCode, setSampleBreakdownCode] = useState(null);
  const overview = aggregateProjects(projects, countMode);
  const sampleBreakdown = overview.samples.find(
    (sample) => sample.code === sampleBreakdownCode,
  ) || null;
  const sampleDenominator = countMode === COUNT_VISITS
    ? overview.totals.samples : overview.totals.patients;
  const analysisCount = overview.analyses.length;
  const acrossProjects = scope !== 'project';
  const groupLabel = acrossProjects
    ? tx('ui.projects.projects_53e890d5')
    : tx('ui.projects.subprojects_4884f24d');
  const metrics = [
    {
      label: tx('ui.projects.patients_bade30a4'), value: overview.totals.patients,
      Icon: UserRound, primary: true,
    },
    ...(acrossProjects ? [{
      label: tx('ui.projects.projects_53e890d5'), value: overview.totals.projects,
      Icon: FolderOpen,
    }] : []),
    {
      label: tx('ui.projects.subprojects_4884f24d'),
      value: acrossProjects ? overview.totals.subprojects : overview.totals.projects,
      Icon: Layers3,
    },
    {
      label: tx('ui.projects.samples_0fb59422'), value: overview.totals.samples,
      Icon: TestTube2,
    },
    {
      label: tx('ui.uploads.analyses_f864c045'), value: overview.totals.analyses,
      Icon: Workflow,
    },
  ];

  return (
    <div className="xo-project-overview-dashboard">
      <dl className="xo-project-overview-metrics">
        {metrics.map(({ label, value, Icon, primary }) => (
          <div key={label} className={primary ? 'is-primary' : ''}>
            <span aria-hidden="true"><Icon /></span>
            <dt>{label}</dt><dd>{value.toLocaleString()}</dd>
          </div>
        ))}
      </dl>
      <div className="xo-project-overview-panels">
        <div className="xo-project-overview-sample-column">
          {overview.samples.length > 0 && <SampleSplitChart samples={overview.samples} />}
          <section className="xo-project-overview-panel is-samples">
            <header>
              <span aria-hidden="true"><TestTube2 /></span>
              <div><h2>{tx('ui.projects.samples_0fb59422')}</h2>
                <p>{overview.totals.samples.toLocaleString()} {tx('ui.projects.samples_0fb59422')}</p></div>
            </header>
            {overview.samples.length === 0 ? (
              <div className="xo-project-coverage-empty">
                <TestTube2 aria-hidden="true" />
                <span>{tx('ui.projects.nothing_uploaded_c05ea45e')}</span>
              </div>
            ) : (
              <div className="xo-project-overview-coverage is-samples">
                {overview.samples.map((sample) => (
                  <div className="xo-project-sample-entry" key={sample.code}>
                    <CoverageRow name={sample.name} code={sample.code}
                      count={sample.count} total={sampleDenominator} />
                    <SampleProjectLeaders sample={sample} groupLabel={groupLabel}
                      onOpen={() => setSampleBreakdownCode(sample.code)} />
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
        <section className="xo-project-overview-panel is-analyses">
          <header>
            <span aria-hidden="true"><Workflow /></span>
            <div><h2>{tx('ui.nav.pipelines_c5df1e14')}</h2>
              <p>{overview.totals.analyses.toLocaleString()} {tx('ui.uploads.analyses_f864c045')}</p></div>
            <b>{analysisCount.toLocaleString()}</b>
          </header>
          {overview.analyses.length === 0 ? (
            <div className="xo-project-coverage-empty">
              <Workflow aria-hidden="true" />
              <span>{tx('ui.projects.nothing_run_4a071f44')}</span>
            </div>
          ) : (
            <div className="xo-project-overview-coverage is-analyses">
              {overview.analyses.map((pipeline) => (
                <section className="xo-project-coverage-group"
                  key={pipeline.pipelineId || pipeline.pipeline}>
                  <header><Workflow aria-hidden="true" />
                    <strong>{pipeline.pipeline}</strong>
                    <small>{pipeline.count.toLocaleString()} {tx('ui.values.completed').toLowerCase()}</small>
                  </header>
                  {pipeline.samples.map((sample) => (
                    <CoverageRow key={`${pipeline.pipelineId || pipeline.pipeline}:${sample.code}`}
                      name={sample.name} code={sample.code} count={sample.count}
                      total={pipeline.count} />
                  ))}
                </section>
              ))}
            </div>
          )}
        </section>
      </div>
      {sampleBreakdown !== null && (
        <SampleProjectsModal sample={sampleBreakdown} groupLabel={groupLabel}
          onClose={() => setSampleBreakdownCode(null)} />
      )}
    </div>
  );
}

function SubprojectList({ subprojects, onSelect, countMode }) {
  return (
    <div className="xo-project-subproject-list">
      {subprojects.map((subproject) => (
        <SubprojectCard key={subproject.id} subproject={subproject}
          onSelect={onSelect} countMode={countMode} />
      ))}
    </div>
  );
}

function ProjectPatientSearch({
  projectId = null,
  projects = [],
  subprojects = [],
  term,
  onTermChange,
  onOpenPatient,
  prominent = false,
  showInput = true,
  revision = 0,
}) {
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({
    status: 'idle', items: [], hasMore: false, error: '',
  });

  useEffect(() => {
    const query = term.trim();
    if (query.length < SEARCH_MIN_LENGTH) {
      setState({ status: 'idle', items: [], hasMore: false, error: '' });
      return undefined;
    }
    const controller = new AbortController();
    setState({ status: 'waiting', items: [], hasMore: false, error: '' });
    const timer = window.setTimeout(() => {
      setState({ status: 'loading', items: [], hasMore: false, error: '' });
      searchPatients(query, { projectId, limit: 20, signal: controller.signal })
        .then(({ items, hasMore }) => {
          if (controller.signal.aborted) return;
          setState({ status: 'ready', items, hasMore, error: '' });
        })
        .catch((error) => {
          if (controller.signal.aborted || error?.name === 'AbortError') return;
          setState({
            status: 'failed', items: [], hasMore: false, error: errorMessage(error),
          });
        });
    }, SEARCH_DELAY_MS);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [attempt, projectId, revision, term]);

  const projectName = (patient) => {
    const project = projects.find((candidate) => candidate.id === patient.parentProjectId);
    return project?.name || tx('ui.projects.project_number', { id: patient.parentProjectId });
  };
  const subprojectName = (patient) => {
    const subproject = subprojects.find((candidate) => candidate.id === patient.subProjectId);
    return subproject?.projectId || '#' + patient.subProjectId;
  };
  const title = projectId === null
    ? tx('ui.projects.find_patient_anywhere') : tx('ui.projects.find_patient_in_project');
  const subtitle = projectId === null
    ? tx('ui.projects.search_all_projects_description')
    : tx('ui.projects.search_project_description');
  const searching = state.status === 'waiting' || state.status === 'loading';

  return (
    <section className={'xo-project-search-surface is-' + state.status
      + (prominent ? ' is-global' : '')}>
      {showInput && <header>
        {!prominent && <div><h2>{title}</h2><p>{subtitle}</p></div>}
        <div className="xo-project-search-controls">
          <SearchField className="xo-project-search-input" value={term} onChange={onTermChange}
            label={title} placeholder={tx('ui.projects.type_at_least_3_characters_b66ade21')}
            autoComplete="off">
            {searching && <Loader2 className="xo-spin" aria-hidden="true" />}
          </SearchField>
        </div>
      </header>}
      {!showInput && searching && <Loading label={tx('ui.projects.reading_patients_1c66498d')} />}
      {state.status === 'idle' && !prominent && (
        <p className="xo-project-search-help">
          {tx('ui.projects.type_at_least_3_characters_b66ade21')}
        </p>
      )}
      {state.status === 'failed' && (
        <div className="xo-project-search-error" role="alert">
          <CircleAlert aria-hidden="true" /><span>{state.error}</span>
          <button type="button" onClick={() => setAttempt((value) => value + 1)}>
            {tx('ui.parts.try_again_042c862e')}
          </button>
        </div>
      )}
      {state.status === 'ready' && state.items.length === 0 && (
        <div className="xo-project-search-empty">
          <UserRound aria-hidden="true" />
          <span><strong>{tx('ui.projects.no_matching_patients_5eab426b')}</strong>
            <small>{tx('ui.projects.try_another_name_or_identity_detail_fc56e849')}</small></span>
        </div>
      )}
      {state.status === 'ready' && state.items.length > 0 && (
        <div className="xo-project-search-results xo-search-results">
          <p>{tx('ui.projects.search_matches', {
            count: String(state.items.length) + (state.hasMore ? '+' : ''),
          })}</p>
          <div>
            {state.items.map((patient) => {
              const location = projectId === null ? projectName(patient) : subprojectName(patient);
              return (
                <button key={patient.id} type="button"
                  onClick={() => onOpenPatient({
                    ...patient,
                    subProject: { id: patient.subProjectId, code: subprojectName(patient) },
                  })}
                  aria-label={`${tx('ui.projects.show_patient_details')}: ${patient.name || patient.id}`}>
                  <span className="xo-project-search-person" aria-hidden="true"><UserRound /></span>
                  <span className="xo-project-search-identity">
                    <strong>{patient.name || tx('ui.projects.patient_id_9a234232', { id: patient.id })}</strong>
                    <small>#{patient.id}{patient.personalId
                      ? tx('ui.projects.personalid_01299c75', { personalId: patient.personalId }) : ''}</small>
                  </span>
                  <span className="xo-project-search-match">
                    <small>{tx('ui.projects.matched_1bf3ec5b')}</small>
                    <strong>{MATCH_LABEL_KEY[patient.matchedOn]
                      ? tx(MATCH_LABEL_KEY[patient.matchedOn]) : tx('ui.common.unknown')}</strong>
                  </span>
                  <span className="xo-project-search-contact">
                    <strong>{patient.email || patient.phone || '—'}</strong>
                    <small>{patient.dateOfBirth || '—'}</small>
                  </span>
                  <span className="xo-project-search-location">
                    <strong>{location}</strong>
                    {projectId === null && <small>{subprojectName(patient)}</small>}
                  </span>
                  <ChevronRight aria-hidden="true" />
                </button>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}

// Fifty patients in a table meant a 780px-wide row that scrolled sideways and
// asked the reader to compare columns nobody was comparing. What a person
// actually does here is find one patient and open them, so each patient is a
// card: identity first, then the two numbers that say whether there is
// anything inside worth opening.
// A row of chips per dimension, capped, with the overflow counted. Three short
// rows say more about a patient than two totals did: which visits exist, what
// material was taken, and which pipelines actually produced something.
function ChipRow({ label, items, limit = 4 }) {
  if (items.length === 0) return null;
  const shown = items.slice(0, limit);
  const rest = items.length - shown.length;
  return (
    <span className="xo-patient-chip-row">
      <i>{label}</i>
      <span>
        {shown.map((item) => <em key={item}>{item}</em>)}
        {rest > 0 && <em className="is-more">+{rest}</em>}
      </span>
    </span>
  );
}

function PatientCard({ patient, onOpen }) {
  const uploads = Array.isArray(patient.uploads) ? patient.uploads : [];
  const visits = [...new Set(uploads
    .map(({ visitNumber }) => visitNumber)
    .filter((visit) => visit !== null))].sort((a, b) => a - b);
  const samples = [...new Set(Array.isArray(patient.sampleTypes)
    ? patient.sampleTypes.map(({ name, code }) => name || code) : [])];
  const pipelines = [...new Set(uploads
    .flatMap(({ runs }) => (Array.isArray(runs) ? runs : []))
    .filter(({ state, runStatus }) => state === 'complete' && runStatus === 'succeeded')
    .map(({ pipeline }) => pipeline)
    .filter(Boolean))];
  const name = patient.name || tx('ui.projects.patient_id_9a234232', { id: patient.id });
  const sex = patient.gender ? codeLabel('gender', patient.gender) : tx('ui.common.unknown');
  const empty = visits.length === 0 && samples.length === 0 && pipelines.length === 0;
  return (
    <button type="button" className="xo-patient-chip" onClick={() => onOpen(patient)}>
      <span className="xo-patient-chip-head">
        <Avatar name={name} />
        <span className="xo-patient-chip-who">
          <span className="xo-patient-chip-name">
            <strong>{name}</strong>
            {patient.usesApp === true && (
              <span className="xo-patient-chip-app" role="img" aria-label={tx('ui.projects.uses_app')} title={tx('ui.projects.uses_app')}>
                <Smartphone aria-hidden="true" />
              </span>
            )}
          </span>
          <small>
            {patient.patientId || `#${patient.id}`} · {patient.subProject?.code || '—'}
            {patient.dateOfBirth ? ` · ${patient.dateOfBirth}` : ''} · {sex}
            {patient.organizationReference ? ` · ${tx('ui.org.reference_short', { reference: patient.organizationReference })}` : ''}
          </small>
        </span>
        <ChevronRight aria-hidden="true" />
      </span>
      <span className="xo-patient-chip-rows">
        <ChipRow label={tx('ui.projects.visits')}
          items={visits.map((visit) => tx('ui.projects.visit_number', { visitNumber: visit }))} />
        <ChipRow label={tx('ui.projects.samples_0fb59422')} items={samples} />
        <ChipRow label={tx('ui.projects.pipeline_runs')} items={pipelines} />
        {empty && <span className="xo-patient-chip-empty">{tx('ui.projects.no_results_yet')}</span>}
      </span>
    </button>
  );
}

// Grid and list are the same card at two densities. List drops to one column
// and lays the facts out along the row, which is what someone scanning a long
// project wants; grid is for recognising a patient at a glance.
function PatientList({ patients, onOpen, view }) {
  return (
    <div className={`xo-patient-chips is-${view}`}>
      {patients.map((patient) => (
        <PatientCard key={patient.id} patient={patient} onOpen={onOpen} />
      ))}
    </div>
  );
}

// Both dialogs are deliberately small: one field set, one action, no wizard.
// Adding a patient or a subproject is a single decision, and the portal had no
// way to do either without going into the database.
// Rendered in place rather than through a portal. #xo.xo-shell sets no
// transform, filter or contain, so a fixed overlay inside it still covers the
// viewport, and staying inside the shell is what gives the dialog the real
// controls: the custom select, the shared buttons, the input styling and every
// design token. A portal would put it outside all of them.
function SmallDialog({ title, subtitle, busy, error, submitLabel, onSubmit, onClose, children }) {
  useEffect(() => {
    const close = (event) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [onClose]);
  return (
    <div className="xo-dialog-layer" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <form className="xo-dialog" onSubmit={onSubmit} role="dialog" aria-modal="true"
        aria-label={title}>
        <header className="xo-dialog-head">
          <div><h2>{title}</h2>{subtitle && <p>{subtitle}</p>}</div>
          <button type="button" className="xo-dialog-close" onClick={onClose}
            aria-label={tx('ui.parts.close')}><X aria-hidden="true" /></button>
        </header>
        <div className="xo-dialog-body">{children}</div>
        {error && <p className="xo-alert t-bad" role="alert">{error}</p>}
        <footer className="xo-dialog-foot">
          <button type="button" className="xo-btn-2" onClick={onClose}>
            {submitLabel === null ? tx('ui.parts.close') : tx('ui.parts.cancel')}
          </button>
          {submitLabel !== null && (
            <button type="submit" className="xo-btn" disabled={busy}>
              {busy && <Loader2 className="xo-spin" aria-hidden="true" />}{submitLabel}
            </button>
          )}
        </footer>
      </form>
    </div>
  );
}

function NewPatientDialog({ subprojects, defaultSubproject, onCreated, onClose }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subProjectId, setSubProjectId] = useState(
    subprojects.find(({ projectId }) => projectId === defaultSubproject)?.id
      || subprojects[0]?.id || '',
  );
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // A patient of an organization's subproject can carry their reference.
  const organization = subprojects.find(({ id }) => String(id) === String(subProjectId))?.organization || null;
  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      onCreated(await createPatient({ subProjectId: Number(subProjectId), name, email,
        organizationReference: organization && reference.trim() !== '' ? reference.trim() : undefined }));
    } catch (caught) {
      setError(errorMessage(caught));
      setBusy(false);
    }
  };
  return (
    <SmallDialog title={tx('ui.projects.new_patient')} subtitle={tx('ui.projects.new_patient_detail')}
      busy={busy} error={error} submitLabel={tx('ui.projects.new_patient')}
      onSubmit={submit} onClose={onClose}>
      <label><span>{tx('ui.projects.subproject')}</span>
        <XomicsSelect value={String(subProjectId)}
          onChange={(event) => setSubProjectId(event.target.value)}
          aria-label={tx('ui.projects.subproject')}>
          {subprojects.map((subproject) => (
            <option key={subproject.id} value={String(subproject.id)}>{subproject.projectId}</option>
          ))}
        </XomicsSelect>
      </label>
      <label><span>{tx('ui.projects.patient')}</span>
        <input type="text" value={name} onChange={(event) => setName(event.target.value)}
          required maxLength={120} autoComplete="off" />
      </label>
      <label><span>{tx('ui.projects.email')}</span>
        <input type="email" value={email} onChange={(event) => setEmail(event.target.value)}
          maxLength={190} autoComplete="off" />
      </label>
      {organization && (
        <label><span>{tx('ui.org.reference_label', { organization: organization.name })}</span>
          <input type="text" value={reference} onChange={(event) => setReference(event.target.value)}
            maxLength={128} autoComplete="off" />
        </label>
      )}
    </SmallDialog>
  );
}

function SubprojectsDialog({ project, subprojects, onCreated, onClose }) {
  const { permissions } = useXomicsAuth();
  const mayLinkOrganization = permissions.includes('ORGANIZATION_READ');
  const [adding, setAdding] = useState(false);
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [organizationId, setOrganizationId] = useState('');
  const [organizations, setOrganizations] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!adding || !mayLinkOrganization) return undefined;
    let active = true;
    loadOrganizations({ active: 'active' })
      .then((result) => { if (active) setOrganizations(result.organizations); })
      .catch(() => { if (active) setOrganizations([]); });
    return () => { active = false; };
  }, [adding, mayLinkOrganization]);
  const submit = async (event) => {
    event.preventDefault();
    if (!adding) return;
    setBusy(true);
    setError('');
    try {
      onCreated(await createSubproject(project.id, { projectId: code, description,
        organizationId: organizationId === '' ? null : Number(organizationId) }));
      setCode('');
      setDescription('');
      setOrganizationId('');
      setAdding(false);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  };
  return (
    <SmallDialog title={tx('ui.projects.manage_subprojects')} subtitle={project.name}
      busy={busy} error={error}
      submitLabel={adding ? tx('ui.projects.add_subproject') : null}
      onSubmit={submit} onClose={onClose}>
      <div className="xo-subproject-list">
        {subprojects.map((subproject) => (
          <div key={subproject.id}>
            <strong>{subproject.projectId}</strong>
            <small>{subproject.description || '—'}{subproject.organization ? ` · ${subproject.organization.name}` : ''}</small>
            <b>{subproject.patientCount.toLocaleString()}</b>
          </div>
        ))}
        {subprojects.length === 0 && <p>{tx('ui.projects.no_subprojects_yet')}</p>}
      </div>
      {adding ? (
        <div className="xo-subproject-add">
          <label><span>{tx('ui.projects.subproject_code')}</span>
            {/* Subproject codes are upper case everywhere they are displayed,
                so the field produces them that way rather than rejecting the
                user's typing after the fact. */}
            <input type="text" value={code} required maxLength={10}
              pattern="[A-Z0-9_-]{2,10}" autoComplete="off" autoFocus
              className="xo-code-input"
              onChange={(event) => setCode(event.target.value.toUpperCase())} />
          </label>
          <label><span>{tx('ui.projects.description')}</span>
            <input type="text" value={description} maxLength={2000} autoComplete="off"
              onChange={(event) => setDescription(event.target.value)} />
          </label>
          {mayLinkOrganization && (
            <label><span>{tx('ui.org.subproject_organization')}</span>
              <XomicsSelect value={organizationId} onChange={(event) => setOrganizationId(event.target.value)}
                aria-label={tx('ui.org.subproject_organization')}>
                <option value="">{tx('ui.org.no_organization')}</option>
                {organizations.map((organization) => (
                  <option key={organization.id} value={String(organization.id)}>{organization.name} · {organization.code}</option>
                ))}
              </XomicsSelect>
              <small>{tx('ui.org.link_rule')}</small>
            </label>
          )}
        </div>
      ) : (
        <button type="button" className="xo-btn-2 xo-subproject-add-open"
          onClick={() => setAdding(true)}>
          <Plus aria-hidden="true" /> {tx('ui.projects.add_subproject')}
        </button>
      )}
    </SmallDialog>
  );
}

function ProjectDetail({
  project, initialSubproject, maySearch, mayManageExamples,
  mayManagePatients, mayManageProjects, onBack,
}) {
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedCode, setSelectedCode] = useState(initialSubproject);
  const [afterIds, setAfterIds] = useState([0]);
  const [term, setTerm] = useState('');
  const [view, setView] = useState('grid');
  const [scopeOpen, setScopeOpen] = useState(false);
  const [addingPatient, setAddingPatient] = useState(false);
  const [managingSubprojects, setManagingSubprojects] = useState(false);
  const [managingSettings, setManagingSettings] = useState(false);
  const scopeRef = useRef(null);
  const afterId = afterIds[afterIds.length - 1];
  const chooseSubproject = (code) => {
    setTerm('');
    setSelectedCode(code);
    setAfterIds([0]);
    setScopeOpen(false);
  };
  useEffect(() => {
    if (!scopeOpen) return undefined;
    const close = (event) => {
      if (!scopeRef.current || scopeRef.current.contains(event.target)) return;
      setScopeOpen(false);
    };
    window.addEventListener('mousedown', close);
    return () => window.removeEventListener('mousedown', close);
  }, [scopeOpen]);
  const [countMode, setCountMode] = useState(COUNT_PATIENTS);
  const [showingInformation, setShowingInformation] = useState(false);
  const loadSubs = useCallback(() => loadSubprojects(project.id), [project.id]);
  const subs = useLiveResource(['projects'], loadSubs);
  // Typing filters the tiles in place across the whole project rather than
  // swapping the cards for the estate-wide result list. Held one step behind
  // the input so a keystroke does not become a request.
  const typed = maySearch ? term.trim() : '';
  const [query, setQuery] = useState('');
  useEffect(() => {
    if (typed.length < SEARCH_MIN_LENGTH) {
      setQuery('');
      return undefined;
    }
    const timer = window.setTimeout(() => setQuery(typed), SEARCH_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [typed]);

  const loadPage = useCallback(async () => (query !== ''
    ? {
      scope: selectedCode,
      query,
      afterId,
      ...await searchProjectPatients(project.id, query, { limit: PAGE }),
    }
    : {
      scope: selectedCode,
      query: '',
      ...(selectedCode === ALL_SUBPROJECTS
        ? await loadProjectPatients(project.id, { limit: PAGE, afterId })
        : await loadSubprojectPatients(project.id, selectedCode, { limit: PAGE, afterId })),
    }), [project.id, selectedCode, afterId, query]);
  // A patient's card shows their uploads and analyses as well as the patient.
  const page = useLiveResource(['projects', 'uploads', 'jobs'], loadPage);
  const subprojects = subs.data === null ? [] : subs.data;
  // Each subproject already arrives with its own samples, runs and analyses, so
  // the project dashboard is the estate dashboard reading those rows instead of
  // the parent projects. A subproject holds no subprojects of its own.
  const subprojectRows = subprojects.map((subproject) => ({
    id: subproject.id,
    name: subproject.projectId,
    subProjectCount: 0,
    patientCount: subproject.patientCount,
    samples: subproject.samples,
    runs: subproject.runs,
    analyses: subproject.analyses,
  }));
  const currentPage = page.data !== null
    && page.data.scope === selectedCode && page.data.afterId === afterId
    && page.data.query === query ? page.data : null;
  const patients = currentPage === null ? [] : currentPage.items;
  const searching = query !== '';
  // A term too short to search, or one the debounce has not sent yet, must not
  // leave the unfiltered project on screen looking like the result.
  const pending = typed !== '' && (query === '' || typed !== query);
  const changeSearch = (value) => {
    setTerm(value);
    setSelectedCode(ALL_SUBPROJECTS);
    setAfterIds([0]);
  };
  const loading = page.loading && currentPage === null;
  const error = page.error === null ? '' : page.error;
  const pageIndex = afterIds.length - 1;
  const selectedSubproject = subprojects.find(
    (subproject) => subproject.projectId === selectedCode,
  );
  const total = selectedCode === ALL_SUBPROJECTS
    ? project.patientCount : selectedSubproject?.patientCount ?? null;
  const previousPage = () => {
    setAfterIds((ids) => (ids.length > 1 ? ids.slice(0, -1) : ids));
  };
  const nextPage = () => {
    if (currentPage?.nextAfterId === null || currentPage?.nextAfterId === undefined) return;
    setAfterIds((ids) => ids.concat(currentPage.nextAfterId));
  };

  return (
    <div className="xo-projects-page is-detail">
      <div className="xo-project-detail-head">
        <button type="button" className="xo-project-back" onClick={onBack}
          aria-label={tx('ui.projects.all_projects_403b2169')}>
          <ChevronLeft aria-hidden="true" />
        </button>
        <Head title={project.name}
          subtitle={selectedSubproject?.description
            || tx('ui.projects.project_id_every_patient_across_its_subprojects_10dbc604', { id: project.id })}>
          {mayManageProjects && <button type="button" className="xo-btn-2" onClick={() => setManagingSettings(true)}>
            <Settings2 aria-hidden="true" />{tx('ui.projects.settings.title')}
          </button>}
        </Head>
      </div>

      <div className="xo-product-navigation">
        <div className="xo-product-toolbar">
          {maySearch && (
            <SearchField className="xo-product-search" value={term} onChange={changeSearch}
              label={tx('ui.projects.find_patient_in_project')}
              placeholder={tx('ui.projects.type_at_least_3_characters_b66ade21')}
              autoComplete="off" />
          )}
          {mayManagePatients && (
            <button type="button" className="xo-btn xo-product-new-button"
              onClick={() => setAddingPatient(true)}>
              <Plus aria-hidden="true" /> {tx('ui.projects.new_patient')}
            </button>
          )}
        </div>
        <div className="xo-product-section-row">
          {subprojectRows.length > 0 && (
            <button type="button" className="xo-btn-2 xo-project-information-button"
              onClick={() => setShowingInformation(true)}>
              <ChartPie aria-hidden="true" /> {tx('ui.projects.project_information')}
            </button>
          )}
          {mayManagePatients && (
            <button type="button" className="xo-btn-2 xo-project-manage-subprojects"
              onClick={() => setManagingSubprojects(true)}>
              <FolderOpen aria-hidden="true" /> {tx('ui.projects.manage_subprojects')}
            </button>
          )}
          <div className="xo-product-toolbar-actions">
            <div className="xo-product-filter-control" ref={scopeRef}>
              <button type="button"
                className={`xo-product-filter-button${selectedCode !== ALL_SUBPROJECTS ? ' is-filtered' : ''}`}
                aria-haspopup="menu" aria-expanded={scopeOpen}
                onClick={() => setScopeOpen((open) => !open)}>
                <Layers3 aria-hidden="true" />
                <span>{selectedCode === ALL_SUBPROJECTS
                  ? tx('ui.projects.all_patients') : selectedCode}</span>
                <b>{(total === null ? 0 : total).toLocaleString()}</b>
                <ChevronDown aria-hidden="true" />
              </button>
              {scopeOpen && (
                <div className="xo-product-filter-menu" role="menu">
                  <button type="button" role="menuitemradio"
                    aria-checked={selectedCode === ALL_SUBPROJECTS}
                    className={selectedCode === ALL_SUBPROJECTS ? 'is-active' : ''}
                    onClick={() => chooseSubproject(ALL_SUBPROJECTS)}>
                    <span>{tx('ui.projects.all_patients')}</span>
                    <b>{project.patientCount.toLocaleString()}</b>
                    {selectedCode === ALL_SUBPROJECTS && <Check aria-hidden="true" />}
                  </button>
                  {subprojects.map((subproject) => (
                    <button type="button" role="menuitemradio" key={subproject.id}
                      aria-checked={selectedCode === subproject.projectId}
                      className={selectedCode === subproject.projectId ? 'is-active' : ''}
                      onClick={() => chooseSubproject(subproject.projectId)}>
                      <span>{subproject.projectId}</span>
                      <b>{subproject.patientCount.toLocaleString()}</b>
                      {selectedCode === subproject.projectId && <Check aria-hidden="true" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <ViewSwitch value={view} onChange={setView} />
          </div>
        </div>
      </div>

      <section className="xo-project-patients">
        {subs.error && <Failed message={subs.error} onRetry={subs.refresh} />}
        <div className="xo-data-surface">
          {error !== '' && <Failed message={error} onRetry={page.refresh} />}
          {(loading || pending) && error === '' && (
            <Loading label={tx('ui.projects.reading_patients_1c66498d')} />
          )}
          {!loading && !pending && error === '' && patients.length === 0 && (
            searching
              ? <Empty title={tx('ui.projects.no_matching_patients_5eab426b')}
                detail={tx('ui.projects.try_another_name_or_identity_detail_fc56e849')} />
              : <Empty title={tx('ui.projects.no_patients_here_aa179679')}
                detail={selectedCode === ALL_SUBPROJECTS
                  ? tx('ui.projects.this_project_has_no_patients_yet_89d160fb')
                  : tx('ui.projects.this_subproject_has_no_patients_yet_b7971573')} />
          )}
          {!loading && !pending && error === '' && patients.length > 0 && (
            <PatientList patients={patients} onOpen={setSelectedPatient} view={view} />
          )}
          {!loading && !pending && error === '' && !searching && (
            <CursorPager pageIndex={pageIndex} count={patients.length} total={total}
              hasMore={currentPage?.hasMore === true}
              busy={page.loading || currentPage === null}
              onPrevious={previousPage} onNext={nextPage} />
          )}
        </div>
      </section>
      {selectedPatient && (
        <PatientDetailModal patient={selectedPatient} mayManageExamples={mayManageExamples}
          onClose={() => setSelectedPatient(null)} onEdited={page.refresh} />
      )}
      {addingPatient && (
        <NewPatientDialog subprojects={subprojects} defaultSubproject={selectedCode}
          onClose={() => setAddingPatient(false)}
          onCreated={() => { setAddingPatient(false); page.refresh(); subs.refresh(); }} />
      )}
      {showingInformation && (
        <ProjectInformationModal project={project} subprojects={subprojectRows}
          countMode={countMode} onCountMode={setCountMode}
          onClose={() => setShowingInformation(false)} />
      )}
      {managingSettings && <ProjectSettingsDialog project={project} onClose={() => setManagingSettings(false)} />}
      {managingSubprojects && (
        <SubprojectsDialog project={project} subprojects={subprojects}
          onClose={() => setManagingSubprojects(false)}
          onCreated={() => subs.refresh()} />
      )}
    </div>
  );
}

export default function XomicsProjects() {
  const { permissions } = useXomicsAuth();
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  // Counts the edits made to a patient opened from the search, so its results are read again.
  const [patientEdits, setPatientEdits] = useState(0);
  const [settingsProject, setSettingsProject] = useState(null);
  const [creatingProject, setCreatingProject] = useState(false);
  const [detailSubproject, setDetailSubproject] = useState(ALL_SUBPROJECTS);
  const [catalogueView, setCatalogueView] = useState('overview');
  const [countMode, setCountMode] = useState(COUNT_PATIENTS);
  const load = useCallback(() => loadParentProjects(), []);
  const list = useLiveResource(['projects'], load);
  const projects = list.data === null ? [] : list.data;
  const loading = list.loading && list.data === null;
  const error = list.error === null ? '' : list.error;
  const maySearch = permissions.includes('PATIENT_IDENTITY_READ');
  const mayManageExamples = permissions.includes('PATIENT_EXAMPLE_DATA_MANAGE');
  const mayManagePatients = permissions.includes('PATIENT_IDENTITY_MANAGE');
  const mayManageProjects = permissions.includes('PROJECT_MANAGE');
  const orderedProjects = [...projects].sort(compareProjectIds);
  const subprojectCount = projects.reduce(
    (sum, project) => sum + (Number(project.subProjectCount) || 0), 0,
  );
  const loadCatalogueSubprojects = useCallback(async () => {
    const parentProjects = list.data === null ? [] : list.data;
    const groups = await Promise.all(parentProjects.map(async (project) => {
      const items = await loadSubprojects(project.id);
      return items.map((subproject) => ({ ...subproject, parentProject: project }));
    }));
    return groups.flat().sort((left, right) => {
      const parentOrder = compareProjectIds(left.parentProject, right.parentProject);
      return parentOrder || String(left.projectId).localeCompare(
        String(right.projectId), undefined, { numeric: true },
      );
    });
  }, [list.data]);
  const catalogueSubprojects = useLiveResource(['projects'], loadCatalogueSubprojects, {
    enabled: projects.length > 0,
  });
  const subprojects = catalogueSubprojects.data === null ? [] : catalogueSubprojects.data;
  const subprojectsLoading = catalogueSubprojects.loading
    && catalogueSubprojects.data === null;
  const subprojectsError = catalogueSubprojects.error === null
    ? '' : catalogueSubprojects.error;
  const openProject = (project, subproject = ALL_SUBPROJECTS) => {
    setDetailSubproject(subproject);
    setSelectedProject(project);
  };
  const closeProject = () => {
    setSelectedProject(null);
    setDetailSubproject(ALL_SUBPROJECTS);
  };

  // ?project=<id> opens a project and ?patient=<id> a patient; quick search
  // links here. Each is read once, then dropped from the address. A patient is
  // found through the same patient search this page uses, by its exact id.
  const [searchParams, setSearchParams] = useSearchParams();
  const [linkError, setLinkError] = useState('');
  const linkedProject = searchParams.get('project');
  const linkedPatient = searchParams.get('patient');
  useEffect(() => {
    if (linkedProject === null || list.data === null) return;
    const project = list.data.find((candidate) => String(candidate.id) === linkedProject);
    if (project === undefined) {
      setLinkError(tx('ui.search.linked_project_missing'));
    } else {
      setLinkError('');
      setDetailSubproject(ALL_SUBPROJECTS);
      setSelectedProject(project);
    }
    const next = new URLSearchParams(searchParams);
    next.delete('project');
    setSearchParams(next, { replace: true });
  }, [linkedProject, list.data, searchParams, setSearchParams]);
  useEffect(() => {
    if (linkedPatient === null) return undefined;
    const id = Number(linkedPatient);
    const next = new URLSearchParams(searchParams);
    next.delete('patient');
    if (!maySearch || !Number.isSafeInteger(id) || id < 1) {
      setLinkError(tx('ui.search.linked_patient_missing'));
      setSearchParams(next, { replace: true });
      return undefined;
    }
    const controller = new AbortController();
    searchPatients(String(id), { limit: 5, signal: controller.signal })
      .then((result) => {
        const patient = result.items.find((item) => Number(item.id) === id);
        if (patient === undefined) {
          setLinkError(tx('ui.search.linked_patient_missing'));
        } else {
          setLinkError('');
          setSelectedProject(null);
          setSelectedPatient(patient);
        }
        setSearchParams(next, { replace: true });
      })
      .catch((requestError) => {
        if (controller.signal.aborted) return;
        setLinkError(errorMessage(requestError));
        setSearchParams(next, { replace: true });
      });
    return () => controller.abort();
  }, [linkedPatient, maySearch, searchParams, setSearchParams]);

  if (selectedProject) {
    return (
      <ProjectDetail project={selectedProject}
        initialSubproject={detailSubproject}
        maySearch={maySearch} mayManageExamples={mayManageExamples}
        mayManagePatients={mayManagePatients} mayManageProjects={mayManageProjects} onBack={closeProject} />
    );
  }
  return (
    <div className="xo-projects-page">
      <Head title={tx('ui.projects.projects_53e890d5')}
        subtitle={tx('ui.projects.every_parent_project_and_what_it_holds_5a8c8dd0')}>
        {mayManageProjects && <button type="button" className="xo-btn" onClick={() => setCreatingProject(true)}>
          <Plus aria-hidden="true" />{tx('ui.projects.settings.new_project')}
        </button>}
      </Head>
      {linkError && <p className="xo-alert t-bad" role="alert">{linkError}</p>}
      {creatingProject && <NewProjectDialog onClose={() => setCreatingProject(false)} onCreated={project => {
        setCreatingProject(false); list.refresh(); openProject(project);
      }} />}
      {settingsProject && <ProjectSettingsDialog project={settingsProject} onClose={() => setSettingsProject(null)} />}
      {maySearch && (
        <ProjectPatientSearch projects={projects} onOpenPatient={setSelectedPatient}
          term={searchTerm} onTermChange={setSearchTerm} revision={patientEdits}
          subprojects={subprojects} prominent />
      )}
      {!loading && !error && projects.length > 0 && (
        <div className="xo-project-catalogue-controls">
          <ProjectViewTabs value={catalogueView} projectCount={projects.length}
            subprojectCount={subprojectCount} onChange={setCatalogueView} />
          <ProjectCountMode value={countMode} onChange={setCountMode} />
        </div>
      )}
      <section className="xo-project-catalogue">
        <div className={'xo-project-catalogue-body is-' + catalogueView}
          id="xo-project-view-panel" role="tabpanel"
          aria-labelledby={`xo-project-view-${catalogueView}`}>
          {error && <Failed message={error} onRetry={list.refresh} />}
          {loading && !error && <Loading label={tx('ui.projects.reading_projects_c4c76423')} />}
          {!loading && !error && projects.length === 0 && (
            <Empty title={tx('ui.projects.no_projects_24a5368b')}
              detail={tx('ui.projects.nothing_has_been_created_yet_11a33130')} />
          )}
          {!loading && !error && projects.length > 0 && catalogueView === 'overview' && (
            <ProjectOverview projects={projects} countMode={countMode} />
          )}
          {!loading && !error && projects.length > 0 && catalogueView === 'projects' && (
            <ProjectList projects={orderedProjects} subprojects={subprojects}
              subprojectsLoading={subprojectsLoading} subprojectsError={subprojectsError}
              onRetrySubprojects={catalogueSubprojects.refresh} onSelect={openProject}
              countMode={countMode} onSettings={mayManageProjects ? setSettingsProject : null} />
          )}
          {!loading && !error && projects.length > 0 && catalogueView === 'subprojects' && (
            <>
              {subprojectsError && (
                <Failed message={subprojectsError} onRetry={catalogueSubprojects.refresh} />
              )}
              {subprojectsLoading && !subprojectsError && (
                <Loading label={tx('ui.projects.reading_subprojects')} />
              )}
              {!subprojectsLoading && !subprojectsError && subprojects.length === 0 && (
                <Empty title={tx('ui.projects.subprojects_4884f24d')}
                  detail={tx('ui.projects.nothing_has_been_created_yet_11a33130')} />
              )}
              {!subprojectsLoading && !subprojectsError && subprojects.length > 0 && (
                <SubprojectList subprojects={subprojects} onSelect={openProject}
                  countMode={countMode} />
              )}
            </>
          )}
        </div>
      </section>
      {selectedPatient && (
        <PatientDetailModal patient={selectedPatient} mayManageExamples={mayManageExamples}
          onClose={() => setSelectedPatient(null)} onEdited={() => setPatientEdits((count) => count + 1)} />
      )}
    </div>
  );
}
